// React & Hooks
import { memo, useCallback, useEffect, useMemo, useState } from "react";

// Helpers
import { useTranslate } from "helpers";

// Validacija
import { Errors } from "services/forms/validation/types";
import { validateYup } from "services/forms/validation/validation";
import { FLEET_VEHICLE_DRIVER_EDIT_FORM_SCHEMA } from "./validation";

// Servisni sloj
import {
  createFleetVehicleDriverAPI,
  getFleetVehicleDriverAPI,
  updateFleetVehicleDriverAPI,
} from "services/api/fleet/vehicles/drivers/api";
import { initialFleetVehicleDriverEditForm } from "services/api/fleet/vehicles/drivers/consts";
import {
  DriverSourceEnum,
  FleetVehicleDriverEditFormValues,
} from "services/api/fleet/vehicles/drivers/types";

// UI
import { Loader } from "ui/components/Loader";
import { Modal, ModalIntro } from "ui/components/Modal";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalHeader from "ui/components/Modal/ModalHeader";
import { Wizard } from "ui/components/Wizard";
import WizardFooter from "ui/components/Wizard/WizardFooter";
import WizardStep from "ui/components/Wizard/WizardStep";
import WizardStepBar from "ui/components/Wizard/WizardStepBar";
import invoiceAnimation from "ui/theme/animations/invoice.json";

// Lokalne komponente
import DriverDocumentForm from "./components/DriverDocumentForm/DriverDocumentForm";
import DriverPersonForm from "./components/DriverPersonForm/DriverPersonForm";
import { DRIVER_DOCUMENT_STEPS } from "./consts";
import { VehicleDriverEditProps } from "./types";

/**
 * Unos vozača. Prvi korak kaže tko je vozač, a svaka isprava ima svoj korak s brojem,
 * rokom i preslikom — jedan ekran po ispravi umjesto jednog dugog obrasca.
 */
const VehicleDriverEditCard: React.FC<VehicleDriverEditProps> = (props) => {
  const { show, vehicleId, driverId, isFullScreen = true, onSuccess, onClose } = props;

  const { t } = useTranslate();

  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [stepCurrent, setStepCurrent] = useState<number>(0);

  const [formValues, setFormValues] = useState<FleetVehicleDriverEditFormValues>(
    initialFleetVehicleDriverEditForm
  );

  // Kod izmjene izvor dolazi iz zatečenog zapisa; kod unosa kreće od korisnika aplikacije.
  const [source, setSource] = useState<DriverSourceEnum>(DriverSourceEnum.USER);

  useEffect(() => {
    if (!show) return;

    setFormValues(initialFleetVehicleDriverEditForm);
    setSource(DriverSourceEnum.USER);
    setStepCurrent(0);
    setErrors({});

    if (driverId) {
      fetchDriver(driverId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, driverId]);

  const fetchDriver = async (id: string) => {
    setIsLoading(true);

    try {
      const result = await getFleetVehicleDriverAPI(id);

      setFormValues(result);
      setSource(result.contactId ? DriverSourceEnum.CONTACT : DriverSourceEnum.USER);
    } catch {
      setFormValues(initialFleetVehicleDriverEditForm);
    }

    setIsLoading(false);
  };

  const handleFieldsChange = useCallback(
    (values: FleetVehicleDriverEditFormValues) => {
      setFormValues(values);
    },
    []
  );

  const handleFinish = useCallback(async () => {
    const isValid = validateYup(
      formValues,
      FLEET_VEHICLE_DRIVER_EDIT_FORM_SCHEMA,
      setErrors
    );

    // Osoba je na prvom koraku — greška se vidi tek kad se tamo vratimo.
    if (!isValid) {
      setStepCurrent(0);
      return;
    }

    setIsLoading(true);

    try {
      const result =
        vehicleId && driverId
          ? await updateFleetVehicleDriverAPI(vehicleId, driverId, formValues)
          : await createFleetVehicleDriverAPI(vehicleId, formValues);

      if (result && onSuccess) onSuccess(result.id);
    } catch {
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
  }, [formValues, vehicleId, driverId, onSuccess]);

  // Osoba je jedino što se traži; isprave se mogu dopuniti kasnije.
  const handleNextStep = useCallback(() => {
    if (stepCurrent === 0) {
      const isValid = validateYup(
        formValues,
        FLEET_VEHICLE_DRIVER_EDIT_FORM_SCHEMA,
        setErrors
      );
      if (!isValid) return;
    }

    setStepCurrent((step) => step + 1);
  }, [stepCurrent, formValues]);

  const handleBackStep = useCallback(() => {
    setStepCurrent((step) => step - 1);
  }, []);

  const handleStepClick = useCallback((stepIndex: number) => {
    setStepCurrent(stepIndex);
  }, []);

  // Osoba je jedini obavezan korak; čim je odabrana, isprave su slobodno dostupne.
  const isPersonSelected = !!(formValues.userId || formValues.contactId);

  const statusBar = (
    <WizardStepBar
      stepLabels={["Vozač", ...DRIVER_DOCUMENT_STEPS.map((step) => step.title)]}
      stepCurrent={stepCurrent}
      onStepClick={handleStepClick}
      firstStepCompleted={isPersonSelected}
      mandatoryCount={1}
    />
  );

  const steps = useMemo(
    () => [
      <WizardStep key="person" stepNumber={0} stepCurrent={stepCurrent}>
        {statusBar}
        <DriverPersonForm
          values={formValues}
          errors={errors}
          onChange={handleFieldsChange}
          source={source}
          onSourceChange={setSource}
        />
      </WizardStep>,
      ...DRIVER_DOCUMENT_STEPS.map((step, index) => (
        <WizardStep
          key={step.uploadsField}
          stepNumber={index + 1}
          stepCurrent={stepCurrent}
        >
          {statusBar}
          <DriverDocumentForm
            step={step}
            values={formValues}
            errors={errors}
            onChange={handleFieldsChange}
          />
        </WizardStep>
      )),
    ],
    [stepCurrent, formValues, errors, handleFieldsChange, source, statusBar]
  );

  return (
    <Modal
      show={show}
      onHide={onClose}
      position={isFullScreen ? "side" : "centered"}
      size={isFullScreen ? "full" : "medium"}
      intro={
        isFullScreen
          ? {
              content: (
                <ModalIntro
                  animation={invoiceAnimation}
                  title="Tko vozi ovo vozilo?"
                  description="Vozač je korisnik aplikacije ili osoba iz imenika. Uz njega se čuvaju vozačka, osobna i putovnica — broj, rok i preslika, da rokovi ne prođu neopaženo."
                  show={show}
                />
              ),
            }
          : undefined
      }
    >
      <ModalHeader
        icon="/media/icons/users.svg"
        title={driverId ? "Ažuriranje vozača" : "Novi vozač"}
        onHide={onClose}
      />
      {/* Podnožje je pravi WizardFooter, pa donji razmak za "fixed" podnožje otpada. */}
      <ModalBody fixedFooterSpacing={false}>
        {/* U manjem prozoru sadržaj inače dodiruje rubove, a koraci su različite visine pa
            prozor skače — dodatni razmak i najmanja visina drže ga mirnim. */}
        <div className={isFullScreen ? undefined : "px-6 pb-2 min-h-[60vh] xl:min-h-[600px]"}>
          <Wizard steps={steps} />
        </div>
        <Loader isLoading={isLoading}> </Loader>
      </ModalBody>
      <WizardFooter
        stepCurrent={stepCurrent}
        steps={steps}
        onNextStep={handleNextStep}
        onBackStep={handleBackStep}
        onFinish={handleFinish}
        isLoading={isLoading}
        isReadOnly={false}
      />
    </Modal>
  );
};

export default memo(VehicleDriverEditCard);
